import type {
    /* ActiveMessage, */ FloaterLocation,
    Model,
} from '../../types/mfc'
import {
    checkAndCloseQuickReply,
    closeFloater,
    openQuickReply,
} from '../app-state/modals/state.floater'
import { destroyRoomState } from '../app-state/state.rooms'
import { MessageAction } from '../constants/enums'
import { QUICK_REPLY } from '../constants/floaters'
import { authenticated } from '../mfcLogic/auth'
import {
    Recipients,
    forgetRecipient,
    getRecipients,
    setRecipient,
} from '../mfcLogic/pm'
import {
    PMSTATE,
    setPMBatchLoadingState,
    setPMChatLoadingState,
    setPMChatUnreadState,
    setPMLoadingState,
} from '../state/state.pm'
import { getSlugAndId, getStateUser, slugToID } from '../state/state.users'
import { initVideo } from '../state/state.video'
import i18n from '../utilities/i18n'
import { safeObj } from '../utilities/safe'
import { TransitionTypes } from '../utilities/transitions'
import { loadUserIfNeeded } from './loadUserIfNeeded'
import { loadUserSocket } from './loadUserSocket'
import { goBackToPreviousRoute, goToMessage, goToMessages } from './nav'
import { openError } from './openError'
import { requestClubShowInfo } from './room'
import { messageAction } from './workerActions'

export const openPm = async (
    slug: string,
    options: {
        noVideo?: boolean
        skipSideEffects?: boolean
    } = {},
) => {
    const { noVideo, skipSideEffects } = options
    const { activeID } = PMSTATE
    const target = slug || activeID

    if (!noVideo) {
        // video needs to be initialized synchronously, so we can't use the awaited version
        const user = getStateUser(slugToID[slug]) || ({} as Model)
        const { isBroadcasting } = user
        if (isBroadcasting !== false) {
            initVideo(target)
        }
    }
    const user = await loadUserIfNeeded(target)
    if (!user) {
        openError({ message: i18n.t('chat.pmWithoutValidUsername') })
        return
    }
    const { id, isModel } = user

    clearPMUnreadCount(id)

    if (!skipSideEffects) {
        if (isModel) {
            messageAction(MessageAction.VIDEO_FEED, { id, loading: false })
            requestClubShowInfo(id)
        }
    }
}

export const openMessage = (
    slugOrId: string | number,
    transitionSource = TransitionTypes.NONE,
) => {
    const [userSlug] = getSlugAndId(slugOrId)
    return goToMessage(userSlug, transitionSource)
}

export const closeMessage = (payload: {
    back?: boolean
    openMessages?: boolean
    idToClose?: number
}) => {
    const { activeID } = PMSTATE
    const { back = false, openMessages, idToClose } = safeObj(payload)
    const id = activeID || idToClose

    resumePMUnreadCount(id)
    checkAndCloseQuickReply(id)
    destroyRoomState(id)

    if (back) {
        goBackToPreviousRoute()
        return
    }
    if (openMessages) {
        goToMessages()
    }
}

const restoreMessages = (
    slug: string,
    originalSlug: string,
    recipients: Recipients,
) => {
    if (!recipients) return

    // if we have slugs that dont match, that means a name
    // change happened while we were gone, so we need to account
    // for that and update the recipients and remove the old one
    if (originalSlug && slug !== originalSlug) {
        const { metadata } = safeObj(recipients[originalSlug])
        forgetRecipient(originalSlug)
        const uid = slugToID[slug]
        setRecipient(slug, { ...metadata, uid })
    }
}

const thirtyMinutes = 30 * 60 * 1000

export const restoreQuickReply = () => {
    if (!authenticated()) return

    const sessions = getRecipients()
    const lsSessions = Object.keys(sessions)
    const quickReplySessions = [] as Array<{
        slug: string
        minimized: boolean
        location: FloaterLocation
    }>
    lsSessions.forEach((slug) => {
        const { quickReply } = sessions[slug].metadata
        const {
            open,
            lastInteraction,
            location,
            minimized = true,
        } = safeObj(quickReply)

        if (open && lastInteraction > +new Date() - thirtyMinutes) {
            quickReplySessions.push({ slug, location, minimized })
        }
    })
    const slugs = lsSessions.reverse()
    const combinedSlugs = Array.from(new Set([...slugs]))
    combinedSlugs.forEach((storedSlug) => {
        const next = (currentSlug: string) =>
            restoreMessages(currentSlug, storedSlug, sessions)
        loadUserSocket(storedSlug, next).then(() => {
            const session = quickReplySessions.find(
                (session) => session.slug === storedSlug,
            )
            if (session) {
                const { minimized, location } = session
                openQuickReply(storedSlug, { minimized }, location)
            }
        })
    })
}

export const sendTypingIndicator = (id: number) => {
    messageAction(MessageAction.SEND_TYPING_INDICATOR, { id })
}

export function loadMessageHistory(id, increment?) {
    setPMChatLoadingState(id, true)
    messageAction(MessageAction.LOAD_PMS, { increment, id })
}

export function loadPMUserListHistory() {
    setPMBatchLoadingState(true)
    setPMLoadingState(true)
    messageAction(MessageAction.LOAD_PMS_USER_LIST)
}

export function loadPMPreview(id, shouldLoad) {
    messageAction(MessageAction.LOAD_PM_PREVIEW, { shouldLoad, id })
}

export function resumePMUnreadCount(theirID) {
    messageAction(MessageAction.RESUME_UNREAD_COUNT, { id: theirID })
}

export function pausePMUnreadCount(theirID) {
    messageAction(MessageAction.PAUSE_UNREAD_COUNT, { id: theirID })
}

export function clearPMUnreadCount(theirID) {
    messageAction(MessageAction.CLEAR_UNREAD_COUNT, {
        pause: true,
        id: theirID,
    })
}

export function clearAllPMUnreadCount() {
    messageAction(MessageAction.CLEAR_UNREAD_COUNT, { all: true })
}

export function hidePM(theirID) {
    closeFloater(theirID, QUICK_REPLY)
    closeMessage({ back: false, idToClose: theirID })
    setPMChatUnreadState(theirID, 0)
    messageAction(MessageAction.HIDE, { id: theirID })
}

export function hideAllPMs() {
    Object.keys(PMSTATE.chats).forEach((id) => {
        setPMChatUnreadState(Number(id), 0)
    })

    messageAction(MessageAction.HIDE, { all: true })
}
